/**
 * Staff360PickerPage — Landing page that lists every active staff in the hospital
 * and lets HR/Admin click any one to open their read-only Staff 360° dashboard.
 *
 * Route: /staff-360  (HR / IT Admin / Hospital Admin / Super Admin)
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { staffAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Compass, Search, ChevronRight, Loader2, UserRound, ShieldCheck, AlertCircle } from 'lucide-react';

const Staff360PickerPage = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('all');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const canViewStaff360 = user?.is_super_admin || hasPermission?.('dashboard.staff_360');

  useEffect(() => {
    if (!canViewStaff360) { setLoading(false); return; }
    (async () => {
      try {
        const res = await staffAPI.list();
        setStaff(res.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load staff');
      } finally {
        setLoading(false);
      }
    })();
  }, [canViewStaff360]);

  const departments = useMemo(() => {
    const set = new Set();
    staff.forEach(s => { if (s.department) set.add(s.department); });
    return Array.from(set).sort();
  }, [staff]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return staff.filter(s => {
      if (dept !== 'all' && (s.department || '') !== dept) return false;
      if (!needle) return true;
      const u = s.user || {};
      const hay = [
        u.first_name, u.last_name, u.email, u.user_id, u.role_name,
        s.employee_code, s.designation, s.department,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [staff, q, dept]);

  if (!canViewStaff360) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="staff-360-restricted">
        <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <p className="text-sm text-slate-600 font-semibold">Restricted</p>
        <p className="text-xs text-slate-500 mt-1">Staff 360° requires the <code className="px-1 bg-slate-100 rounded">dashboard.staff_360</code> permission. Your own 360° dashboard is available at <button onClick={() => navigate('/my-360')} className="text-emerald-600 underline">/my-360</button>.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="staff-360-picker">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-md">
            <Compass size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Staff 360°</h1>
            <p className="text-sm text-slate-500">Pick a staff member to open their read-only holistic dashboard — identity, KYC, attendance, leave, salary, performance.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/my-360')} data-testid="staff-360-open-mine">
          <UserRound size={14} className="mr-1.5" /> Open my own 360°
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Search</p>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name, employee ID, email, role…"
                value={q}
                onChange={e => setQ(e.target.value)}
                className="pl-9"
                data-testid="staff-360-search"
              />
            </div>
          </div>
          <div className="min-w-[200px]">
            <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Department</p>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger data-testid="staff-360-dept-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-slate-500 ml-auto" data-testid="staff-360-count">
            <strong>{filtered.length}</strong> of {staff.length} staff
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
          <Loader2 className="animate-spin" size={20} /> Loading staff…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 text-sm">
          No staff match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="staff-360-grid">
          {filtered.map(s => <StaffTile key={s.id} staff={s} backendUrl={backendUrl} onOpen={() => navigate(`/staff-360/${s.user?.id}`)} />)}
        </div>
      )}
    </div>
  );
};

const StaffTile = ({ staff, backendUrl, onOpen }) => {
  const u = staff.user || {};
  const initials = `${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase();
  const photo = staff.profile_photo_url ? `${backendUrl}${staff.profile_photo_url}` : null;
  return (
    <button
      onClick={onOpen}
      className="group bg-white text-left rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-md transition p-4 flex items-center gap-3 w-full"
      data-testid={`staff-360-tile-${u.id}`}
    >
      <div className="h-12 w-12 rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center flex-shrink-0 border border-slate-200">
        {photo ? (
          <img src={photo} alt={initials} className="w-full h-full object-cover" />
        ) : (
          <span className="text-emerald-700 font-bold">{initials || '—'}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-slate-800 truncate group-hover:text-emerald-700">{u.first_name} {u.last_name}</p>
          {staff.status !== 'active' && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{staff.status}</span>}
        </div>
        <p className="text-xs text-slate-500 truncate">{staff.designation || '—'} · {staff.department || '—'}</p>
        <p className="text-[10px] text-slate-400 font-mono mt-0.5 truncate">{u.user_id || staff.employee_code || '—'} · {u.role_name || 'Staff'}</p>
      </div>
      <ChevronRight size={16} className="text-slate-300 group-hover:text-emerald-600 flex-shrink-0" />
    </button>
  );
};

export default Staff360PickerPage;
